/**
 * FinanceTemplatesPage — Issue #368.
 *
 * 财务模板底座管理：列表、详情、字段映射、预览/导出/打印记录、版本发布/弃用/
 * 归档。通过 @jinzhuan/api-client 走真实后端，禁止前端伪状态。
 *
 * 下游表单（付款申请、借支、报销、工资、提成、销售管理报表）由 Issue
 * #369-#374 实现；本页只暴露模板本身。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useFinanceTemplateActions,
  useFinanceTemplateDetail,
  useFinanceTemplates,
  type FinanceTemplateFieldDto,
  type FinanceTemplateSummaryDto,
} from '@/hooks/useFinanceTemplates';

const CATEGORY_LABELS: Record<string, string> = {
  daily_settlement: '日常结算类',
  payroll: '工资核算类',
  order_management: '订单管理类',
};

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  deprecated: '已弃用',
  archived: '已归档',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-800',
  deprecated: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-200 text-gray-500',
};

const SENSITIVE_LEVEL_LABELS: Record<string, string> = {
  none: '普通',
  mask_in_list: '列表脱敏',
  reveal_required: '需 reveal',
  high_audit: '高敏审计',
};

const SENSITIVE_LEVEL_COLORS: Record<string, string> = {
  none: 'bg-gray-50 text-gray-500',
  mask_in_list: 'bg-blue-50 text-blue-700',
  reveal_required: 'bg-amber-50 text-amber-700',
  high_audit: 'bg-red-50 text-red-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function SensitiveBadge({ level }: { level: string }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] ${
        SENSITIVE_LEVEL_COLORS[level] ?? 'bg-gray-50 text-gray-500'
      }`}
    >
      {SENSITIVE_LEVEL_LABELS[level] ?? level}
    </span>
  );
}

function groupBySection(
  fields: readonly FinanceTemplateFieldDto[],
): Array<{ section: string; fields: FinanceTemplateFieldDto[] }> {
  const map = new Map<string, FinanceTemplateFieldDto[]>();
  for (const field of fields) {
    const arr = map.get(field.section) ?? [];
    arr.push(field);
    map.set(field.section, arr);
  }
  return Array.from(map.entries()).map(([section, items]) => ({
    section,
    fields: items.slice().sort((a, b) => a.orderIndex - b.orderIndex),
  }));
}

function TemplateDetailPanel({
  templateId,
  onClose,
  canManage,
  canRender,
}: {
  templateId: string;
  onClose: () => void;
  canManage: boolean;
  canRender: boolean;
}) {
  const { data, loading, error, fetchDetail } = useFinanceTemplateDetail();
  const {
    loading: actionLoading,
    error: actionError,
    recordRender,
    publish,
    deprecate,
    archive,
  } = useFinanceTemplateActions();

  useEffect(() => {
    void fetchDetail(templateId);
  }, [templateId, fetchDetail]);

  async function refresh() {
    await fetchDetail(templateId);
  }

  async function onRecordRender(format: 'preview' | 'excel' | 'pdf' | 'print') {
    if (!data) return;
    const ok = await recordRender({
      templateId: data.id,
      renderFormat: format,
    });
    if (ok) await refresh();
  }

  async function onPublish() {
    if (!data) return;
    const ok = await publish(data.id, data.recordVersion);
    if (ok) await refresh();
  }

  async function onDeprecate() {
    if (!data) return;
    const reason = window.prompt('请输入弃用原因：');
    if (!reason) return;
    const ok = await deprecate(data.id, data.recordVersion, reason);
    if (ok) await refresh();
  }

  async function onArchive() {
    if (!data) return;
    if (!window.confirm('确认归档该模板版本？归档后不可恢复。')) return;
    const ok = await archive(data.id, data.recordVersion);
    if (ok) await refresh();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 py-10">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold">模板详情</h2>
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm"
            onClick={onClose}
          >
            关闭
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          {loading && (
            <div className="text-sm text-gray-500">加载中…</div>
          )}
          {error && (
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {error.message}
            </div>
          )}
          {actionError && (
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {actionError.message}
            </div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">模板编码：</span>
                  <span className="font-mono">{data.templateCode}</span>
                </div>
                <div>
                  <span className="text-gray-500">模板名称：</span>
                  {data.templateName}
                </div>
                <div>
                  <span className="text-gray-500">分类：</span>
                  {CATEGORY_LABELS[data.category] ?? data.category}
                </div>
                <div>
                  <span className="text-gray-500">业务版本号：</span>v{data.version}
                </div>
                <div>
                  <span className="text-gray-500">状态：</span>
                  <StatusBadge status={data.status} />
                </div>
                <div>
                  <span className="text-gray-500">原模板文件：</span>
                  {data.sourceFileName ?? '—'}
                </div>
                <div>
                  <span className="text-gray-500">发布时间：</span>
                  {data.publishedAt
                    ? new Date(data.publishedAt).toLocaleString()
                    : '—'}
                </div>
                <div>
                  <span className="text-gray-500">乐观锁：</span>
                  {data.recordVersion}
                </div>
              </div>
              {data.description && (
                <div className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  {data.description}
                </div>
              )}

              <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4">
                {canRender && data.status === 'published' && (
                  <>
                    <button
                      disabled={actionLoading}
                      onClick={() => void onRecordRender('preview')}
                      className="rounded border border-gray-300 px-3 py-1 text-sm"
                    >
                      记录预览
                    </button>
                    <button
                      disabled={actionLoading}
                      onClick={() => void onRecordRender('excel')}
                      className="rounded border border-gray-300 px-3 py-1 text-sm"
                    >
                      记录导出 Excel
                    </button>
                    <button
                      disabled={actionLoading}
                      onClick={() => void onRecordRender('pdf')}
                      className="rounded border border-gray-300 px-3 py-1 text-sm"
                    >
                      记录导出 PDF
                    </button>
                    <button
                      disabled={actionLoading}
                      onClick={() => void onRecordRender('print')}
                      className="rounded border border-gray-300 px-3 py-1 text-sm"
                    >
                      记录打印
                    </button>
                  </>
                )}
                {canManage && data.status === 'draft' && (
                  <button
                    disabled={actionLoading}
                    onClick={() => void onPublish()}
                    className="rounded border border-green-300 bg-green-50 px-3 py-1 text-sm text-green-700"
                  >
                    发布
                  </button>
                )}
                {canManage &&
                  (data.status === 'published' ||
                    data.status === 'draft') && (
                    <button
                      disabled={actionLoading}
                      onClick={() => void onDeprecate()}
                      className="rounded border border-yellow-300 bg-yellow-50 px-3 py-1 text-sm text-yellow-700"
                    >
                      弃用
                    </button>
                  )}
                {canManage && data.status !== 'archived' && (
                  <button
                    disabled={actionLoading}
                    onClick={() => void onArchive()}
                    className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-600"
                  >
                    归档
                  </button>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700">
                  字段映射（按区段，保留原 Excel 标签与顺序）
                </h3>
                <div className="mt-2 space-y-3">
                  {groupBySection(data.fields).map(({ section, fields }) => (
                    <div
                      key={section}
                      className="rounded border border-gray-200"
                    >
                      <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600">
                        {section}
                      </div>
                      <table className="min-w-full text-sm">
                        <thead className="bg-white text-gray-500">
                          <tr>
                            <th className="px-3 py-1 text-left text-xs">字段标签</th>
                            <th className="px-3 py-1 text-left text-xs">key</th>
                            <th className="px-3 py-1 text-left text-xs">类型</th>
                            <th className="px-3 py-1 text-left text-xs">敏感等级</th>
                            <th className="px-3 py-1 text-left text-xs">备注</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field) => (
                            <tr key={field.fieldKey} className="border-t border-gray-100">
                              <td className="px-3 py-1">
                                {field.label}
                                {field.required && (
                                  <span className="ml-1 text-red-500">*</span>
                                )}
                              </td>
                              <td className="px-3 py-1 font-mono text-xs text-gray-500">
                                {field.fieldKey}
                              </td>
                              <td className="px-3 py-1 text-xs">{field.dataType}</td>
                              <td className="px-3 py-1">
                                <SensitiveBadge level={field.sensitiveLevel} />
                              </td>
                              <td className="px-3 py-1 text-xs text-gray-500">
                                {field.notes ?? ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FinanceTemplatesPage() {
  const auth = useAuth();
  const permissions = useMemo(
    () => auth.permissions ?? [],
    [auth.permissions],
  );
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.TEMPLATE_READ);
  const canRender = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.TEMPLATE_RENDER,
  );
  const canManage = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.TEMPLATE_MANAGE,
  );

  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, loading, error, fetchList } = useFinanceTemplates();
  const { syncBuiltIn, loading: syncing, error: syncError } =
    useFinanceTemplateActions();

  const refresh = () =>
    fetchList({
      category: categoryFilter || undefined,
      status: statusFilter || undefined,
      search: search || undefined,
    });

  useEffect(() => {
    if (canRead) void refresh();
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">
        您没有查看财务模板的权限。
      </div>
    );
  }

  async function onSync() {
    const result = await syncBuiltIn();
    if (result) {
      await refresh();
      window.alert(`内置模板同步完成：新增 ${result.createdCount} 条`);
    }
  }

  function openDetail(row: FinanceTemplateSummaryDto) {
    setDetailId(row.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">财务模板底座</h1>
        {canManage && (
          <button
            onClick={() => void onSync()}
            disabled={syncing}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {syncing ? '同步中…' : '同步内置模板'}
          </button>
        )}
      </div>

      {syncError && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {syncError.message}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">分类</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="模板编码或名称"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">模板编码</th>
              <th className="px-3 py-2 text-left">模板名称</th>
              <th className="px-3 py-2 text-left">分类</th>
              <th className="px-3 py-2 text-left">版本</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">原 Excel 文件</th>
              <th className="px-3 py-2 text-left">发布时间</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">{row.templateCode}</td>
                <td className="px-3 py-2">{row.templateName}</td>
                <td className="px-3 py-2">
                  {CATEGORY_LABELS[row.category] ?? row.category}
                </td>
                <td className="px-3 py-2">v{row.version}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {row.sourceFileName ?? '—'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {row.publishedAt
                    ? new Date(row.publishedAt).toLocaleString()
                    : '—'}
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => openDetail(row)}
                    className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                  >
                    查看字段
                  </button>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  {canManage
                    ? '暂无财务模板，可点击右上角“同步内置模板”从原 Excel 模板初始化六类底座'
                    : '暂无已发布模板'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailId && (
        <TemplateDetailPanel
          templateId={detailId}
          onClose={() => setDetailId(null)}
          canManage={canManage}
          canRender={canRender}
        />
      )}
    </div>
  );
}
